import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, ClipboardList, Loader2, MessageSquare, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { name: '', description: '', communication_messages: [''], task_ids: [] };
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export const Requirements = ({
  title = 'Requirements',
  singular = 'Requirement',
  endpoint = 'requirements',
  subtitle = 'Manage requirements available while creating leads',
  emptyPrompt = 'Add a requirement before assigning it to a lead',
  testId = 'requirements',
  communicationMessages = false,
  taskSelection,
}) => {
  const enableTaskSelection = taskSelection ?? endpoint === 'requirements';
  const perms = detectPerms();
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const [response, tasksResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/${endpoint}`, authConfig()),
        enableTaskSelection ? axios.get(`${BACKEND_URL}/api/service-tasks`, authConfig()) : Promise.resolve({ data: [] }),
      ]);
      setItems(response.data || []);
      setTasks(tasksResponse.data || []);
    } catch {
      setError(`Failed to fetch ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [enableTaskSelection, endpoint, title]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => {
    setForm({ ...emptyForm, communication_messages: [''], task_ids: [] });
    setEditing(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      description: item.description || '',
      communication_messages: item.communication_messages?.length ? item.communication_messages : [''],
      task_ids: item.task_ids || [],
    });
    setEditing(item);
    setError('');
    setShowModal(true);
  };

  const saveItem = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
    };
    if (communicationMessages) {
      payload.communication_messages = form.communication_messages
        .map(message => message.trim())
        .filter(Boolean);
    }
    if (enableTaskSelection) payload.task_ids = form.task_ids;
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/${endpoint}/${editing.id}`, payload, authConfig());
      } else {
        await axios.post(`${BACKEND_URL}/api/${endpoint}`, payload, authConfig());
      }
      setShowModal(false);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to save ${singular.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/${endpoint}/${deleteTarget.id}`, authConfig());
      setDeleteTarget(null);
      await fetchItems();
    } catch (err) {
      setDeleteTarget(null);
      setError(err.response?.data?.detail || `Failed to delete ${singular.toLowerCase()}`);
    }
  };

  const filteredItems = items.filter((item) => {
    const query = search.trim().toLowerCase();
    return !query
      || item.name?.toLowerCase().includes(query)
      || item.description?.toLowerCase().includes(query);
  });

  const addTask = taskId => taskId && setForm(current => current.task_ids.includes(taskId)
    ? current : { ...current, task_ids: [...current.task_ids, taskId] });
  const removeTask = taskId => setForm(current => ({ ...current, task_ids: current.task_ids.filter(id => id !== taskId) }));
  const moveTask = (index, direction) => setForm(current => {
    const target = index + direction;
    if (target < 0 || target >= current.task_ids.length) return current;
    const ids = [...current.task_ids];
    [ids[index], ids[target]] = [ids[target], ids[index]];
    return { ...current, task_ids: ids };
  });

  return (
    <div data-testid={`${testId}-page`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{title}</h1>
          <p className="text-slate-600 mt-1">{subtitle}</p>
        </div>
        {perms.canAdd && (
          <button type="button" onClick={openCreate} data-testid={`add-${singular.toLowerCase()}-btn`}
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
            <Plus className="h-5 w-5" /> Add {singular}
          </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button type="button" onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <ClipboardList className="h-14 w-14 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No {title.toLowerCase()} found</h3>
            <p className="text-slate-500">{items.length ? 'Try adjusting your search' : emptyPrompt}</p>
          </div>
        ) : (
          <>
            <div className={`hidden md:grid ${enableTaskSelection ? 'grid-cols-7' : 'grid-cols-6'} gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50`}>
              <div className="col-span-2 text-sm font-semibold text-slate-700 uppercase tracking-wider">Name</div>
              <div className="col-span-3 text-sm font-semibold text-slate-700 uppercase tracking-wider">Description</div>
              {enableTaskSelection && <div className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Tasks</div>}
              <div className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Actions</div>
            </div>
            {filteredItems.map((item) => (
              <div key={item.id} data-testid={`${singular.toLowerCase()}-row-${item.id}`}
                className={`grid grid-cols-1 ${enableTaskSelection ? 'md:grid-cols-7' : 'md:grid-cols-6'} gap-4 px-6 py-4 border-b border-slate-100 last:border-0 items-center hover:bg-slate-50`}>
                <div className="md:col-span-2 font-medium text-slate-900">{item.name}</div>
                <div className="md:col-span-3 text-sm text-slate-600">{item.description || '-'}</div>
                {enableTaskSelection && <div className="text-sm text-slate-600">{item.task_ids?.length || 0}</div>}
                <div className="flex items-center gap-1">
                  {perms.canEdit && (
                    <button type="button" onClick={() => openEdit(item)} data-testid={`edit-btn-${item.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {perms.canDelete && (
                    <button type="button" onClick={() => setDeleteTarget(item)} data-testid={`delete-btn-${item.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-sm w-full ${(communicationMessages || enableTaskSelection) ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{editing ? `Edit ${singular}` : `Add ${singular}`}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={saveItem} className="p-6 overflow-y-auto">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-5">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                  <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    required data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {enableTaskSelection && (
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-700">Tasks</label>
                      <span className="text-xs text-slate-500">Choose tasks and arrange their opening order</span>
                    </div>
                    {tasks.some(task => !form.task_ids.includes(task.id)) && (
                      <select value="" onChange={event => addTask(event.target.value)} data-testid="add-requirement-task"
                        className="mb-3 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 focus:border-orange-500 focus:outline-none">
                        <option value="">Add a task...</option>
                        {tasks.filter(task => !form.task_ids.includes(task.id)).map(task => <option key={task.id} value={task.id}>{task.name}</option>)}
                      </select>
                    )}
                    {form.task_ids.length === 0 ? (
                      <div className="rounded-sm border border-dashed border-slate-300 px-4 py-5 text-center text-sm text-slate-500">No tasks selected.</div>
                    ) : (
                      <div className="divide-y divide-slate-100 rounded-sm border border-slate-200">
                        {form.task_ids.map((taskId, index) => {
                          const task = tasks.find(item => item.id === taskId);
                          if (!task) return null;
                          return <div key={taskId} className="flex items-center gap-3 px-3 py-2.5">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-700">{index + 1}</span>
                            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-900">{task.name}</p><p className="truncate text-xs text-slate-500">{task.description || ''}</p></div>
                            <button type="button" onClick={() => moveTask(index, -1)} disabled={index === 0} className="rounded-sm border border-slate-200 p-1.5 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                            <button type="button" onClick={() => moveTask(index, 1)} disabled={index === form.task_ids.length - 1} className="rounded-sm border border-slate-200 p-1.5 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                            <button type="button" onClick={() => removeTask(taskId)} className="rounded-sm border border-red-200 p-1.5 text-red-500"><Trash2 className="h-4 w-4" /></button>
                          </div>;
                        })}
                      </div>
                    )}
                  </div>
                )}
                {communicationMessages && (
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="block text-sm font-medium text-slate-700">Default Communication Messages</label>
                      <button type="button"
                        onClick={() => setForm(current => ({
                          ...current,
                          communication_messages: [...current.communication_messages, ''],
                        }))}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700">
                        <Plus className="h-3.5 w-3.5" /> Add Message
                      </button>
                    </div>
                    <div className="space-y-2">
                      {form.communication_messages.map((message, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <MessageSquare className="h-4 w-4 mt-3 text-slate-400 shrink-0" />
                          <textarea value={message} rows={2}
                            onChange={(event) => setForm(current => ({
                              ...current,
                              communication_messages: current.communication_messages.map((item, itemIndex) => (
                                itemIndex === index ? event.target.value : item
                              )),
                            }))}
                            placeholder={`Default message ${index + 1}`}
                            data-testid={`input-communication-message-${index}`}
                            className="flex-1 px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-y" />
                          <button type="button" aria-label={`Remove message ${index + 1}`}
                            onClick={() => setForm(current => ({
                              ...current,
                              communication_messages: current.communication_messages.length === 1
                                ? ['']
                                : current.communication_messages.filter((_, itemIndex) => itemIndex !== index),
                            }))}
                            className="p-2 mt-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-sm">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Add one or more messages that can be selected while notifying a customer.</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    rows={3} data-testid="input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-3">Delete {singular}?</h3>
            <p className="text-slate-600 mb-6">Delete “{deleteTarget.name}” from the {title} master?</p>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={deleteItem} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
